export function sleep(timeMs: number) {
    return new Promise(res => setTimeout(res, timeMs));
}

type TimerFunctionHandler = (...args: any) => any;

export interface TimerFunction extends TimerFunctionHandler {
    clear(): void;

    destroy(): void;
}

/**
 * Creates a wrapper function that throttles the number of times 'callback' can be called.
 *
 * @param {number} interval - The time to wait between calls of 'callback'
 * @param {function} callback - The callback to be called
 * @param {object} [thisValue] - An object to set as the 'this' variable for the callback, so bind does not have to be used.
 *
 * @returns {TimerFunction} - A wrapper function that will throttle the number of times 'callback' can be called consecutively
 */
export function function_throttle(interval: number, callback: (...args: any) => any, thisValue: any = undefined): TimerFunction {
    let throttle = null;

    let handler = function (...args) {
        const self = typeof thisValue !== 'undefined'
            ? thisValue
            : this;

        callback.apply(self, args);

        setTimer();
    }

    function setTimer() {
        throttle = setTimeout(throttleExpired, interval);
    }

    function throttleExpired() {
        throttle = null;
    }

    function api(...args) {
        if (! throttle) {
            handler.apply(this, args);
        }
    }

    // Allow any timers to be cleared from outside of this function
    api.clear = function () {
        if (throttle) {
            clearTimeout(throttle);

            throttle = null;
        }
    };

    // Useful when used in Vue Components. Destroy in the beforeDestroy() to prevent random errors caused by functions
    // on timers that run after the component is destroyed.
    api.destroy = function () {
        api.clear();

        callback = null;
        self = null;

        // Finally, throw errors if it is used after being destroyed
        api.clear = api.destroy = handler = () => {
            throw new Error('Interaction with destroyed throttled function')
        };
    };

    return api;
}

/**
 * Creates a wrapper function that throttles the number of times 'callback' can be called.
 *
 * @param {number} interval - The time to wait between calls of 'callback'
 * @param {function} callback - The callback to be called
 * @param {object} [thisValue] - An object to set as the 'this' variable for the callback, so bind does not have to be used.
 *
 * @returns {TimerFunction} - A wrapper function that will throttle the number of times 'callback' can be called consecutively
 */
export function function_throttle_initial(interval: number, callback: (...args: any) => any, thisValue: any = undefined): TimerFunction {
    let throttle = null,
        lastArgs: any[]|null = null;

    let handler = function () {
        const self = typeof thisValue !== 'undefined'
            ? thisValue
            : this;

        callback.apply(self, lastArgs);

        api.clear();
    }

    function setTimer() {
        throttle = setTimeout(handler, interval);
    }

    function api(...args) {
        lastArgs = args;

        if (! throttle) {
            setTimer();
        }
    }

    // Allow any timers to be cleared from outside of this function
    api.clear = function () {
        if (throttle) {
            clearTimeout(throttle);

            throttle = null;
            lastArgs = null;
        }
    };

    api.destroy = function () {
        api.clear();

        callback = null;
        self = null;

        // Finally, throw errors if it is used after being destroyed
        api.clear = api.destroy = handler = () => {
            throw new Error('Interaction with destroyed throttled function')
        };
    };

    return api;
}

/**
 * Creates a wrapper function that will execute 'callback', 'delay' milliseconds after calling said wrapper.
 *
 * @param {number} delay - The time to wait before calling the callback
 * @param {function} callback - The callback to be called
 * @param {object} [thisValue] - An object to set as the 'this' variable for the callback, so bind does not have to be used.
 *
 * @returns {TimerFunction} - A wrapper function that will execute 'callback', 'delay' milliseconds after calling this wrapper.
 */
export function function_delay(delay, callback, thisValue = undefined): TimerFunction {
    let timer = null,
        lastArguments,
        self = thisValue;

    const updateThis = function (newThisValue) {
        // If the user has specified a this value, use it instead, otherwise, determine it automatically
        if (typeof thisValue === 'undefined') {
            self = newThisValue;
        }
    };

    const timerFunction = function () {
        timer = null;

        callback.apply(self, lastArguments);

        lastArguments = null;

        updateThis(null);
    };

    let handler = function () {
        lastArguments = arguments;

        if (timer !== null) {
            clearTimeout(timer);
        }

        timer = setTimeout(timerFunction, delay);
    };

    const api = function () {
        // Capture the 'this' context and pass to the original function
        updateThis(this);

        handler.apply(null, arguments);
    };

    // Allow any timers to be cleared from outside of this function
    api.clear = function () {
        if (timer) {
            clearTimeout(timer);

            timer = null;
        }
    };

    // Useful when used in Vue Components. Destroy in the beforeDestroy() to prevent random errors caused by functions
    // on timers that run after the component is destroyed.
    api.destroy = function () {
        api.clear();

        // Replace the handler with a noop
        handler = function () {
            console.warn('Interaction with destroyed delayed function');
        }
    };

    return api;
}
